import { CMS_BASE_URL } from './constants'
import { buildStrapiRequestHeaders, buildStrapiRequestUrl } from './strapiRequest'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type { PlatformPageCmsContent } from './interfaces'

interface FetchPlatformContentOptions {
  signal?: AbortSignal
  previewToken?: string
}

const PLATFORM_PAGE_ENDPOINT = `/api/platform-page?${
  [
    'populate[customizeDeployBackgroundDesktop]=true',
    'populate[customizeDeployBackgroundMobile]=true',
    'populate[customizeDeployFeatures][populate]=*',
    'populate[customizeDeployButton]=true',
    'populate[gearsBackgroundDesktop]=true',
    'populate[gearsBackgroundMobile]=true',
    'populate[gearsFeatures][populate]=*',
    'populate[supportedHardwareCards][populate]=*',
    'populate[integrationIntroIcon]=true',
    'populate[integrationOptions][populate]=*',
    'populate[resourcesButton]=true',
    'populate[alsaImage]=true',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const mapPlatformPageContent = (payload: unknown): PlatformPageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const customizeDeployBackgroundDesktopUrl = readMediaUrl(contentRoot.customizeDeployBackgroundDesktop)
  const customizeDeployBackgroundMobileUrl = readMediaUrl(contentRoot.customizeDeployBackgroundMobile)
  const customizeDeployFeatures = toStrapiEntityCollection(contentRoot.customizeDeployFeatures)
    .flatMap((feature) => {
      const iconUrl = readMediaUrl(feature.icon)
      const title = readString(feature.title)
      const body = readString(feature.body)

      if (iconUrl === null || title === null || body === null) {
        return []
      }

      return [{ iconUrl, title, body }]
    })
  const customizeDeployButton = toStrapiEntity(contentRoot.customizeDeployButton)
  const customizeDeployButtonText = readString(customizeDeployButton?.label)
  const customizeDeployButtonUrl = readString(customizeDeployButton?.url)

  const gearsBackgroundDesktopUrl = readMediaUrl(contentRoot.gearsBackgroundDesktop)
  const gearsBackgroundMobileUrl = readMediaUrl(contentRoot.gearsBackgroundMobile)
  const gearsFeatures = toStrapiEntityCollection(contentRoot.gearsFeatures)
    .flatMap((feature) => {
      const iconUrl = readMediaUrl(feature.icon)
      const title = readString(feature.title)

      if (iconUrl === null || title === null) {
        return []
      }

      return [{ iconUrl, title }]
    })

  const supportedHardwareCards = toStrapiEntityCollection(contentRoot.supportedHardwareCards)
    .flatMap((card) => {
      const iconUrl = readMediaUrl(card.icon)
      const title = readString(card.title)
      const items = toStrapiEntityCollection(card.items)
        .map((item) => readString(item.text))
        .filter((item): item is string => item !== null)

      if (iconUrl === null || title === null || items.length === 0) {
        return []
      }

      return [{ iconUrl, title, items }]
    })

  const integrationIntroIconUrl = readMediaUrl(contentRoot.integrationIntroIcon)
  const integrationOptions = toStrapiEntityCollection(contentRoot.integrationOptions)
    .flatMap((option) => {
      const iconUrl = readMediaUrl(option.icon)
      const text = readString(option.text)

      if (iconUrl === null || text === null) {
        return []
      }

      return [{ iconUrl, text }]
    })
  const resourcesButton = toStrapiEntity(contentRoot.resourcesButton)
  const resourcesButtonText = readString(resourcesButton?.label)
  const resourcesButtonUrl = readString(resourcesButton?.url)

  const alsaImageUrl = readMediaUrl(contentRoot.alsaImage)

  const heroPillText = readString(contentRoot.heroPillText)
  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)
  const customizeDeployTitle = readString(contentRoot.customizeDeployTitle)
  const customizeDeployDescription = readString(contentRoot.customizeDeployDescription)
  const gearsTitle = readString(contentRoot.gearsTitle)
  const gearsDescription = readString(contentRoot.gearsDescription)
  const gettingStartedTitle = readString(contentRoot.gettingStartedTitle)
  const supportedHardwareTitle = readString(contentRoot.supportedHardwareTitle)
  const integrationOptionsTitle = readString(contentRoot.integrationOptionsTitle)
  const integrationIntroTitle = readString(contentRoot.integrationIntroTitle)
  const resourcesNote = readString(contentRoot.resourcesNote)
  const alsaTitle = readString(contentRoot.alsaTitle)
  const alsaCodeBlockHeading = readString(contentRoot.alsaCodeBlockHeading)
  const alsaCodeBlockBody = readString(contentRoot.alsaCodeBlockBody)
  const exploreSoundcoreTitle = readString(contentRoot.exploreSoundcoreTitle)
  const exploreSoundcoreButtonText = readString(contentRoot.exploreSoundcoreButtonText)
  const exploreSoundscapeTitle = readString(contentRoot.exploreSoundscapeTitle)
  const exploreSoundscapeButtonText = readString(contentRoot.exploreSoundscapeButtonText)

  if (
    heroPillText === null ||
    heroTitleText === null ||
    heroDescription === null ||
    customizeDeployTitle === null ||
    customizeDeployDescription === null ||
    customizeDeployBackgroundDesktopUrl === null ||
    customizeDeployBackgroundMobileUrl === null ||
    customizeDeployFeatures.length !== 3 ||
    customizeDeployButtonText === null ||
    customizeDeployButtonUrl === null ||
    gearsTitle === null ||
    gearsDescription === null ||
    gearsBackgroundDesktopUrl === null ||
    gearsBackgroundMobileUrl === null ||
    gearsFeatures.length === 0 ||
    gettingStartedTitle === null ||
    supportedHardwareTitle === null ||
    supportedHardwareCards.length === 0 ||
    integrationOptionsTitle === null ||
    integrationIntroTitle === null ||
    integrationIntroIconUrl === null ||
    integrationOptions.length === 0 ||
    resourcesNote === null ||
    resourcesButtonText === null ||
    resourcesButtonUrl === null ||
    alsaTitle === null ||
    alsaImageUrl === null ||
    alsaCodeBlockHeading === null ||
    alsaCodeBlockBody === null ||
    exploreSoundcoreTitle === null ||
    exploreSoundcoreButtonText === null ||
    exploreSoundscapeTitle === null ||
    exploreSoundscapeButtonText === null
  ) {
    return null
  }

  return {
    hero: {
      pillText: heroPillText,
      titleText: heroTitleText,
      description: heroDescription,
    },
    customizeDeploy: {
      title: customizeDeployTitle,
      description: customizeDeployDescription,
      desktopBackgroundUrl: customizeDeployBackgroundDesktopUrl,
      mobileBackgroundUrl: customizeDeployBackgroundMobileUrl,
      features: customizeDeployFeatures,
      buttonText: customizeDeployButtonText,
      buttonUrl: customizeDeployButtonUrl,
    },
    gears: {
      title: gearsTitle,
      description: gearsDescription,
      desktopBackgroundUrl: gearsBackgroundDesktopUrl,
      mobileBackgroundUrl: gearsBackgroundMobileUrl,
      features: gearsFeatures,
    },
    gettingStarted: {
      title: gettingStartedTitle,
      supportedHardwareTitle,
      supportedHardwareCards,
      integrationOptionsTitle,
      integrationIntroTitle,
      integrationIntroIconUrl,
      integrationOptions,
      resourcesNote,
      resourcesButtonText,
      resourcesButtonUrl,
    },
    alsa: {
      title: alsaTitle,
      imageUrl: alsaImageUrl,
      codeBlockHeading: alsaCodeBlockHeading,
      codeBlockBody: alsaCodeBlockBody,
    },
    exploreProducts: {
      soundcoreTitle: exploreSoundcoreTitle,
      soundcoreButtonText: exploreSoundcoreButtonText,
      soundscapeTitle: exploreSoundscapeTitle,
      soundscapeButtonText: exploreSoundscapeButtonText,
    },
  }
}

export const fetchPlatformContentFromStrapi = async (
  options: FetchPlatformContentOptions = {},
): Promise<PlatformPageCmsContent | null> => {
  const response = await fetch(buildStrapiRequestUrl(PLATFORM_PAGE_ENDPOINT, options.previewToken), {
    method: 'GET',
    signal: options.signal,
    headers: buildStrapiRequestHeaders(options.previewToken),
  })

  if (!response.ok) {
    throw new Error(`Unable to fetch platform CMS content (${response.status})`)
  }

  const payload = await response.json() as unknown

  return mapPlatformPageContent(payload)
}
